// src/features/dashboard/hooks/useEmployeeIdleTime.ts

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useDashboardDate } from '../../context/DashboardDateContext';
import { formatApiDate } from '../../utils/date.utils';

import {
  getEmployeeCode,
} from '../../../auth/utils/Login/authStorage';

import {
  EmployeeIdleKpiResponse,
  IdleMinuteByHourResponse,
  WTDIdleTrendResponse,
  IdleVsTeamResponse,
  IdleDailyDetailResponse,
  EmployeeIdleTimeRequest,
} from '../../types/Employee/employeeIdleTime.types';

import {
  getEmployeeIdleKPIs,
  getIdleMinutesChart,
  getIdleWTDTrend,
  getIdleTeamComparison,
  getIdleDailyDetails,
} from '../../services/Employee/employeeIdleTime.service';

/* =========================================================
   Helpers
========================================================= */

/**
 * Converts:
 *
 * 2026-08-17T00:00:00
 * 2026-08-17
 *
 * into:
 *
 * Mon 17
 *
 * The date always comes from workDate.
 */
const formatWorkDate = (
  workDate: string,
): string => {
  if (!workDate) {
    return '';
  }

  const date = new Date(workDate);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const shortDay = date.toLocaleDateString(
    'en-US',
    {
      weekday: 'short',
    },
  );

  const dayNumber = date.getDate();

  return `${shortDay} ${dayNumber}`;
};

/**
 * Safely converts a number/string to number.
 */
const toNumber = (
  value: unknown,
): number => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue)
    ? numberValue
    : 0;
};

/**
 * Converts:
 *
 * 15
 * "15"
 * "15%"
 *
 * into:
 *
 * 15
 */
const parsePercentage = (
  value: unknown,
): number => {
  if (
    value === null ||
    value === undefined
  ) {
    return 0;
  }

  return toNumber(
    String(value).replace('%', ''),
  );
};

/* =========================================================
   Hook
========================================================= */

export const useEmployeeIdleTime = () => {
  /*
   * IMPORTANT:
   *
   * The selected Dashboard date is the source of truth.
   *
   * DashboardHeader
   *       ↓
   * DashboardDateContext
   *       ↓
   * useEmployeeIdleTime
   */
  const { fromDate } = useDashboardDate();

  const employeeCode =
    getEmployeeCode() || '';

  const [loading, setLoading] =
    useState(false);

  const [kpis, setKpis] =
    useState<EmployeeIdleKpiResponse>();

  const [hourlyIdle, setHourlyIdle] =
    useState<
      IdleMinuteByHourResponse[]
    >([]);

  const [wtdTrend, setWtdTrend] =
    useState<
      WTDIdleTrendResponse[]
    >([]);

  const [teamComparison, setTeamComparison] =
    useState<IdleVsTeamResponse>();

  const [dailyDetails, setDailyDetails] =
    useState<IdleDailyDetailResponse>();

  /* =========================================================
     LOAD DATA
  ========================================================= */

  const loadData = useCallback(
    async () => {
      if (!employeeCode) {
        setLoading(false);
        return;
      }

      setLoading(true);

      /*
       * IMPORTANT:
       *
       * Employee Idle Time API currently accepts
       * a single "date" field.
       *
       * Therefore the selected FROM date is sent here.
       *
       * Do NOT add fromDate/toDate to this request
       * unless the backend API and
       * EmployeeIdleTimeRequest type support them.
       */
      const request: EmployeeIdleTimeRequest = {
        employeeCode,
        date: formatApiDate(fromDate),
      };

      try {
        /* =====================================================
           KPI
        ===================================================== */

        try {
          const response =
            await getEmployeeIdleKPIs(
              request,
            );

          console.log(
            'Employee Idle KPI Response:',
            response,
          );

          setKpis(response);
        } catch (error) {
          console.error(
            'Employee Idle KPI Error:',
            error,
          );

          setKpis(undefined);
        }

        /* =====================================================
           IDLE MINUTES BY HOUR
        ===================================================== */

        try {
          const response =
            await getIdleMinutesChart(
              request,
            );

          console.log(
            'Idle Minutes API Response:',
            response,
          );

          const hourlyResponse:
            IdleMinuteByHourResponse[] =
            Array.isArray(response)
              ? response.map(
                  (item) => ({
                    hour: toNumber(
                      item.hour,
                    ),

                    idleMinutes:
                      toNumber(
                        item.idleMinutes,
                      ),
                  }),
                )
              : [];

          console.log(
            'Mapped Hourly Idle:',
            hourlyResponse,
          );

          setHourlyIdle(
            hourlyResponse,
          );
        } catch (error) {
          console.error(
            'Hourly Idle Error:',
            error,
          );

          setHourlyIdle([]);
        }

        /* =====================================================
           WTD IDLE TREND

           IMPORTANT:

           workDate is the source of truth.

           Example API:

           2026-08-17 → Mon 17
           2026-08-18 → Tue 18
           2026-08-19 → Wed 19
           2026-08-20 → Thu 20
           2026-08-21 → Fri 21
        ===================================================== */

        try {
          const response =
            await getIdleWTDTrend(
              request,
            );

          console.log(
            'WTD Idle Trend API Response:',
            response,
          );

          const trendResponse:
            WTDIdleTrendResponse[] =
            Array.isArray(response)
              ? response.map(
                  (item) => {
                    const workDate =
                      String(
                        item.workDate ??
                          '',
                      );

                    return {
                      ...item,

                      /*
                       * Preserve original API date.
                       */
                      workDate,

                      /*
                       * Use actual workDate.
                       *
                       * Mon 17
                       * Tue 18
                       * Wed 19
                       * Thu 20
                       * Fri 21
                       */
                      displayDay:
                        formatWorkDate(
                          workDate,
                        ),

                      idleMinutes:
                        toNumber(
                          item.idleMinutes,
                        ),

                      targetMinutes:
                        toNumber(
                          item.targetMinutes,
                        ),
                    };
                  },
                )
              : [];

          console.log(
            'Formatted WTD Idle Trend:',
            trendResponse,
          );

          setWtdTrend(
            trendResponse,
          );
        } catch (error) {
          console.error(
            'WTD Trend Error:',
            error,
          );

          setWtdTrend([]);
        }

        /* =====================================================
           ME VS TEAM IDLE COMPARISON
        ===================================================== */

        try {
          const response =
            await getIdleTeamComparison(
              request,
            );

          console.log(
            'Team Comparison API Response:',
            response,
          );

          if (!response) {
            setTeamComparison(
              undefined,
            );
          } else {
            const formattedResponse:
              IdleVsTeamResponse =
              {
                ...response,

                /*
                 * Normalise threshold.
                 */
                idlePercentageThreshold:
                  parsePercentage(
                    response.idlePercentageThreshold,
                  ),

                /*
                 * Preserve workDate if API
                 * provides it.
                 *
                 * Also safely support APIs
                 * where it was previously missing.
                 */
                trend:
                  Array.isArray(
                    response.trend,
                  )
                    ? response.trend.map(
                        (item: any) => {
                          const workDate =
                            String(
                              item.workDate ??
                                '',
                            );

                          return {
                            ...item,

                            /*
                             * Keep workDate.
                             */
                            workDate,

                            dayName:
                              String(
                                item.dayName ??
                                  '',
                              ),

                            myIdlePercentage:
                              toNumber(
                                item.myIdlePercentage,
                              ),

                            teamAvgIdlePercentage:
                              toNumber(
                                item.teamAvgIdlePercentage,
                              ),
                          };
                        },
                      )
                    : [],
              };

            console.log(
              'Formatted Me vs Team Idle:',
              formattedResponse,
            );

            setTeamComparison(
              formattedResponse,
            );
          }
        } catch (error) {
          console.error(
            'Team Comparison Error:',
            error,
          );

          setTeamComparison(
            undefined,
          );
        }

        /* =====================================================
           DAILY DETAILS
        ===================================================== */

        try {
          const response =
            await getIdleDailyDetails(
              request,
            );

          console.log(
            'Daily Details API Response:',
            response,
          );

          setDailyDetails(
            response,
          );
        } catch (error) {
          console.error(
            'Daily Details Error:',
            error,
          );

          setDailyDetails(
            undefined,
          );
        }
      } catch (error) {
        console.error(
          'Employee Idle Time Error:',
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [
      employeeCode,
      fromDate,
    ],
  );

  /* =========================================================
     LOAD WHEN DATE / EMPLOYEE CHANGES
  ========================================================= */

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* =========================================================
     RETURN
  ========================================================= */

  return {
    loading,

    kpis,

    /*
     * WTD data contains:
     *
     * workDate
     * dayLabel
     * displayDay
     * idleMinutes
     * idleTime
     * targetMinutes
     */
    wtdTrend,

    /*
     * Hourly idle data.
     */
    hourlyIdle,

    /*
     * Me vs Team data preserves workDate
     * when the API provides it.
     */
    teamComparison,

    /*
     * Daily details.
     */
    dailyDetails,

    /*
     * Reload manually if required.
     */
    reload: loadData,
  };
};

export default useEmployeeIdleTime;